/**
 * PER-ITEM SHAREABLE KEY (BHM-CDX-DIR-083 §4-5).
 *
 * Cada secreto tiene una DEK independiente derivada del material legítimo del
 * usuario (masterKey, solo en cliente) vía HKDF:
 *
 *   itemDek = HKDF(masterKey, info = 'item-dek:' + itemId)   // 32 bytes
 *
 * Garantías:
 *  - Per-ítem: la DEK de un ítem NO permite derivar la de otro (HKDF info único),
 *    ni recuperar la masterKey (unidireccional). Un share nunca da acceso lateral.
 *  - ZK: el servidor solo ve ciphertext; jamás la masterKey ni la DEK en claro.
 *  - Sin duplicación: los campos NO se copian; se re-cifran in place una sola
 *    vez (migración client-side) cuando un ítem legacy se comparte por primera vez.
 *  - Recepción: el destinatario desenvuelve la DEK (ECDH+HKDF) y descifra los
 *    campos con ella — exactamente lo que ya espera `fetchSharedItem`.
 */
import { deriveSharedKey, decrypt, zeroMemory } from '@barmetvault/crypto';

const decode = (value: string) => Uint8Array.from(atob(value), (c) => c.charCodeAt(0));

/** DEK del ítem derivada de la masterKey (solo cliente). 32 bytes. */
export async function deriveItemDek(masterKey: Uint8Array, itemId: string): Promise<Uint8Array> {
  return deriveSharedKey(new Uint8Array([...masterKey]), `item-dek:${itemId}`);
}

/**
 * Determina la clave con la que están cifrados los campos del ítem:
 * la DEK por ítem si ya fue migrada, o la masterKey si es legacy.
 * Nunca lanza: `probe` es un campo cifrado (nameEnc).
 */
export async function itemEncryptionKey(
  masterKey: Uint8Array,
  itemId: string,
  probe: unknown,
): Promise<{ key: Uint8Array; legacy: boolean; dek: Uint8Array }> {
  const dek = await deriveItemDek(masterKey, itemId);
  const packed = toBytes(probe);
  if (packed.length > 40) {
    try {
      decrypt(packed.slice(24), packed.slice(0, 24), dek);
      return { key: dek, legacy: false, dek };
    } catch {
      // legacy: los campos van con la masterKey
    }
  }
  return { key: new Uint8Array([...masterKey]), legacy: true, dek };
}

function toBytes(value: unknown): Uint8Array {
  if (!value) return new Uint8Array();
  if (value instanceof Uint8Array) return value;
  if (typeof value === 'string') return decode(value);
  if (Array.isArray(value)) return Uint8Array.from(value);
  if (typeof value === 'object' && 'data' in (value as Record<string, unknown>)) {
    const data = (value as { data: unknown }).data;
    if (Array.isArray(data)) return Uint8Array.from(data);
  }
  return new Uint8Array();
}

export { toBytes, zeroMemory };
